import React from "react";
import styled from "styled-components";

const ModalOverlay = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.8);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1000;
    padding: 20px;
`;

const ModalContent = styled.div`
    background: white;
    border-radius: 12px;
    max-width: 90vw;
    max-height: 90vh;
    overflow: hidden;
    position: relative;
    width: 900px;

    @media (max-width: 768px) {
        width: 100%;
        max-height: 80vh;
        max-width: 95vw;
    }
`;

const CloseButton = styled.button`
    position: absolute;
    top: 15px;
    right: 15px;
    background: rgba(0, 0, 0, 0.7);
    color: white;
    border: none;
    border-radius: 50%;
    width: 40px;
    height: 40px;
    font-size: 1.2rem;
    cursor: pointer;
    z-index: 10;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: background-color 0.3s ease;

    &:hover {
        background: rgba(0, 0, 0, 0.9);
    }
`;

const VideoContainer = styled.div`
    width: 100%;
    height: 0;
    padding-bottom: 56.25%; /* 16:9 aspect ratio */
    position: relative;
    background: #000;
`;

const YouTubeIframe = styled.iframe`
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    border: none;
`;

const VideoInfo = styled.div`
    padding: 1.5rem;

    @media (max-width: 768px) {
        padding: 1rem;
    }
`;

const VideoTitle = styled.h2`
    font-size: 1.5rem;
    font-weight: 600;
    margin-bottom: 0.5rem;
    color: #333;

    @media (max-width: 768px) {
        font-size: 1.2rem;
    }
`;

const VideoDescription = styled.p`
    color: #666;
    line-height: 1.6;
    margin-bottom: 1rem;

    @media (max-width: 768px) {
        font-size: 0.9rem;
    }
`;

const VideoMeta = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 1rem;
    justify-content: space-between;
    align-items: center;
    font-size: 0.9rem;
    color: #999;

    @media (max-width: 768px) {
        flex-direction: column;
        align-items: flex-start;
        gap: 0.5rem;
    }
`;

const VideoStats = styled.div`
    display: flex;
    gap: 1rem;
    align-items: center;

    @media (max-width: 768px) {
        flex-wrap: wrap;
        gap: 0.5rem;
    }
`;

const Category = styled.span`
    background: #667eea;
    color: white;
    padding: 0.3rem 0.8rem;
    border-radius: 15px;
    font-size: 0.8rem;
    font-weight: 500;
`;

const StatItem = styled.span`
    color: #666;
    font-size: 0.85rem;
`;

const Date = styled.span`
    color: #999;
    font-size: 0.85rem;
`;

interface Content {
    id: number;
    title: string;
    description: string;
    content_type: "works" | "product" | "pr" | "hero_video";
    category_id?: number;
    category_name?: string;
    category_color?: string;
    youtube_url: string;
    youtube_id: string;
    thumbnail_url: string;
    duration: string;
    published_at: string;
    view_count: number;
    like_count: number;
    custom_thumbnail: string;
    custom_description: string;
    custom_published_at: string;
    is_active: boolean;
    sort_order: number;
    created_at: string;
    updated_at: string;
}

interface YouTubeModalProps {
    content: Content | null;
    isOpen: boolean;
    onClose: () => void;
}

const YouTubeModal: React.FC<YouTubeModalProps> = ({ content, isOpen, onClose }) => {

    // Extract YouTube video ID and create embed URL
    const getYouTubeEmbedUrl = (url: string): string => {
        if (!url) return "";

        // If we already have the video ID (from backend), use it directly
        if (content?.youtube_id) {
            return `https://www.youtube.com/embed/${content.youtube_id}?autoplay=1&rel=0&modestbranding=1&fs=1&cc_load_policy=0&iv_load_policy=3&enablejsapi=1`;
        }

        let videoId = "";

        // Handle different YouTube URL formats with more precise regex
        const patterns = [
            /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/,
            /youtube\.com\/v\/([^&\n?#]+)/,
            /youtube\.com\/watch\?.*&v=([^&\n?#]+)/,
            /youtube\.com\/shorts\/([^&\n?#]+)/
        ];

        for (const pattern of patterns) {
            const match = url.match(pattern);
            if (match) {
                videoId = match[1];
                break;
            }
        }

        if (videoId) {
            return `https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0&modestbranding=1&fs=1&cc_load_policy=0&iv_load_policy=3&enablejsapi=1`;
        }

        return "";
    };

    const formatDate = (dateString: string) => {
        if (!dateString) return "";
        return dateString.split('T')[0];
    };

    const formatNumber = (num: number) => {
        return num > 0 ? num.toLocaleString() : "";
    };

    const handleOverlayClick = (e: React.MouseEvent) => {
        if (e.target === e.currentTarget) {
            onClose();
        }
    };

    const handleKeyDown = (e: KeyboardEvent) => {
        if (e.key === "Escape") {
            onClose();
        }
    };

    React.useEffect(() => {
        if (isOpen) {
            document.addEventListener("keydown", handleKeyDown);
            return () => document.removeEventListener("keydown", handleKeyDown);
        }
    }, [isOpen]);

    if (!isOpen || !content) return null;

    const embedUrl = getYouTubeEmbedUrl(content.youtube_url);

    return (
        <ModalOverlay onClick={handleOverlayClick}>
            <ModalContent>
                <CloseButton onClick={onClose}>✕</CloseButton>

                <VideoContainer>
                    {embedUrl ? (
                        <YouTubeIframe
                            src={embedUrl}
                            title={content.title}
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                            allowFullScreen
                            referrerPolicy="strict-origin-when-cross-origin"
                        />
                    ) : (
                        <div style={{
                            position: "absolute",
                            top: "50%",
                            left: "50%",
                            transform: "translate(-50%, -50%)",
                            color: "white",
                            textAlign: "center"
                        }}>
                            Video not available
                        </div>
                    )}
                </VideoContainer>

                <VideoInfo>
                    <VideoTitle>{content.title}</VideoTitle>
                    <VideoDescription>
                        {content.custom_description || content.description}
                    </VideoDescription>
                    <VideoMeta>
                        <VideoStats>
                            {content.category_name && <Category>{content.category_name}</Category>}
                            {content.duration && <StatItem>⏱️ {content.duration}</StatItem>}
                            {content.view_count > 0 && <StatItem>👁️ {formatNumber(content.view_count)} views</StatItem>}
                            {content.like_count > 0 && <StatItem>👍 {formatNumber(content.like_count)} likes</StatItem>}
                        </VideoStats>
                        <Date>
                            {formatDate(content.custom_published_at || content.published_at || content.created_at)}
                        </Date>
                    </VideoMeta>
                </VideoInfo>
            </ModalContent>
        </ModalOverlay>
    );
};

export default YouTubeModal;